import React, { useRef, useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import TopNavigation from '@amzn/awsui-components-react/polaris/top-navigation';
import { useAuth } from './contexts/AuthContext';
import { useAdminAuth } from './hooks/useAdminAuth';
import logo from './images/AWS_logo.png';
import { projectTitle } from './constants';
import { UTILITIES } from './constants';
import useNotificationMenu from './components/NotificationMenu';

// Pages
import Homepage from './pages/Homepage';
import {StatsPage} from './pages/stats';
import {UploadFilesPage} from './pages/uploadFiles';
// import SecurityTXTPage from './pages/security';
import {AboutPage} from './pages/about';
import AnalyticsDashboard from './pages/analytics/AnalyticsDashboard';
import Unauthorized from './pages/Unauthorized';
import LoginContent from './components/LoginContent';
import FeedbackModal, { FeedbackModalRef } from './components/FeedbackModal';

const App: React.FC = () => {
  const { username } = useAuth();
  const { isAdmin } = useAdminAuth();
  const feedbackModalRef = useRef<FeedbackModalRef>(null);
  
  // Get dynamic notification menu
  const notificationMenu = useNotificationMenu();
  
  // Filter utilities based on admin status
  const filteredUtilities = UTILITIES.filter(utility => {
    // Only show Analytics button to admin users
    if (utility.text === 'Analytics') {
      return isAdmin;
    }
    return true;
  });
  
  // Create utilities array with dynamic notifications
  const dynamicUtilities = [
    notificationMenu, // Dynamic notification menu
    ...filteredUtilities.slice(1) // All other filtered utilities except the static notification one
  ];

  // Debug: Check if ref is set after component mounts
  useEffect(() => {
    console.log("App mounted, feedbackModalRef exists:", !!feedbackModalRef.current);
    
    // Add a global click handler for debugging
    const handleGlobalClick = (e: MouseEvent) => {
      // Check if the click target is a link with href="#feedback"
      const target = e.target as HTMLElement;
      const feedbackLink = target.closest('a[href="#feedback"]');
      if (feedbackLink) {
        console.log("Global click handler caught feedback link click");
        e.preventDefault();
        if (feedbackModalRef.current) {
          console.log("Opening feedback modal from global handler");
          feedbackModalRef.current.open();
        }
      }
    };
    
    document.addEventListener('click', handleGlobalClick);
    return () => document.removeEventListener('click', handleGlobalClick);
  }, []);

  // Handle top navigation utility item clicks
  const handleUtilityClick = (e: any) => {
    console.log("Utility clicked:", e.detail);
    
    // Handle external links
    if (e.detail && e.detail.external) {
      // Let the default behavior handle external links
      return;
    }
    
    // Try multiple ways to identify the feedback button
    if (
      (e.detail && e.detail.id === 'feedback-button') || 
      (e.detail && e.detail.href === '#feedback') ||
      (e.detail && e.detail.text === 'Feedback')
    ) {
      e.preventDefault();
      console.log("Feedback button clicked - match found");
      if (feedbackModalRef.current) {
        console.log("Attempting to open feedback modal");
        feedbackModalRef.current.open();
      } else {
        console.error("Feedback modal ref is null");
      }
    }
  };

  return (
    <div data-testid="app-container">
      {username ? (
        <>
          <TopNavigation
            identity={{
              href: '/',
              title: projectTitle,
              logo: { src: logo, alt: 'MediaCaption Corner logo' },
            }}
            utilities={dynamicUtilities}
            onUtilityClick={handleUtilityClick}
          />
          
          {/* Always render the FeedbackModal but keep it hidden until opened via ref */}
          <FeedbackModal 
            ref={feedbackModalRef} 
            onDismiss={() => console.log("Modal dismissed")}
          />
          
          <Routes>
            <Route path="/" element={<Homepage />} />
            <Route path="/stats" element={<StatsPage />} />
            <Route path="/uploadfiles" element={<UploadFilesPage />} />
            <Route path="/analytics" element={<AnalyticsDashboard />} />
            <Route path="/unauthorized" element={<Unauthorized />} />
            {/* <Route path="/.well-known/security.txt" element={<SecurityTXTPage />} /> */}
            <Route path="/about" element={<AboutPage />} />
          </Routes>
        </>
      ) : (
        <LoginContent />
      )}
    </div>
  );
};

export default App;
